import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import toast from 'react-hot-toast'
import { getMyUpcomingMatches, submitPublicPrediction } from '../services/publicService'
import type { UpcomingMatchItem } from '../services/publicService'
import { ScoreEnum, PredictionTypeEnum, getValueByPredictionType } from '../types/prediction'
import { getMatchDate } from '../utils/dateHelper'

const PredictionBoard: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [items, setItems] = useState<UpcomingMatchItem[]>([])
  const [loading, setLoading] = useState(true)
  const [predictingMatchId, setPredictingMatchId] = useState<string | null>(null)
  const [winner, setWinner] = useState<string | null>(null)
  const [predictedScore, setPredictedScore] = useState<string>(ScoreEnum.OneZero)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getMyUpcomingMatches()
      .then((list) => {
        if (!cancelled) setItems(list)
      })
      .catch(() => {
        if (!cancelled) setItems([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  const now = Date.now()
  const upcomingItems = items.filter((item) => {
    const kickoff = new Date(getMatchDate(item.match, true)).getTime()
    const isNotFinished = !item.match.score || item.match.winner === undefined
    return kickoff > now && isNotFinished
  })

  const handleSubmitPrediction = async (item: UpcomingMatchItem) => {
    if (!winner) return
    const { match, publicTournamentId, publicParticipantId } = item
    let scoreToSend = predictedScore
    if (winner === 'teamB' && predictedScore.includes('-')) {
      const [a, b] = predictedScore.split('-').map((s) => s.trim())
      scoreToSend = `${b}-${a}`
    }
    const winnerTeam =
      winner === 'teamA' ? match.teamA : winner === 'teamB' ? match.teamB : null
    setPredictingMatchId(match.id)
    try {
      await submitPublicPrediction(publicTournamentId, {
        publicParticipantId,
        matchId: match.id,
        winner: winnerTeam ?? undefined,
        predictedScore: scoreToSend,
      })
      toast.success(t('publicHub.predictionSaved'))
      setWinner(null)
      setPredictedScore(ScoreEnum.OneZero)
      getMyUpcomingMatches().then(setItems)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('playPublic.loadError'))
    } finally {
      setPredictingMatchId(null)
    }
  }

  const scoreOptions =
    winner === 'draw' || winner === null
      ? getValueByPredictionType(PredictionTypeEnum.Draw)
      : getValueByPredictionType(PredictionTypeEnum.Win)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 container mx-auto px-4 pb-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft size={18} /> {t('dashboard.backToDashboard')}
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="font-display text-xl sm:text-2xl font-bold text-foreground">
            {t('publicHub.predictionBoard')}
          </h1>
          <Link
            to="/public/leaderboard"
            className="inline-flex items-center px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium"
          >
            {t('publicHub.personalLeaderboard')}
          </Link>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {t('publicHub.predictionBoardHint')}
        </p>

        <div className="space-y-4">
          {upcomingItems.length === 0 ? (
            <p className="text-muted-foreground py-8">{t('publicHub.noUpcomingMatches')}</p>
          ) : (
            upcomingItems.map((item) => (
              <div key={item.match.id} className="glass-card rounded-xl p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-primary/20 text-primary mb-1 sm:mb-0">
                    {item.tournamentName}
                  </span>
                  <span className="text-sm text-muted-foreground">{item.match.date} {item.match.time}</span>
                </div>
                <div className="font-medium text-foreground mb-2">
                  {item.match.teamA} vs {item.match.teamB}
                </div>
                <div className="flex flex-wrap gap-2 items-center mt-2">
                  <select
                    value={winner ?? ''}
                    onChange={(e) => setWinner(e.target.value || null)}
                    className="px-2 py-1 rounded-lg border border-muted bg-background text-foreground text-sm"
                  >
                    <option value="">-- {t('publicHub.winner')} --</option>
                    <option value="teamA">{item.match.teamA}</option>
                    <option value="teamB">{item.match.teamB}</option>
                    <option value="draw">Draw</option>
                  </select>
                  <select
                    value={predictedScore}
                    onChange={(e) => setPredictedScore(e.target.value)}
                    className="px-2 py-1 rounded-lg border border-muted bg-background text-foreground text-sm"
                  >
                    {scoreOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleSubmitPrediction(item)}
                    disabled={!winner || predictingMatchId === item.match.id}
                    className="px-3 py-1 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
                  >
                    {predictingMatchId === item.match.id ? t('common.loading') : t('publicHub.makePrediction')}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  )
}

export default PredictionBoard
